import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import { RecipientPicker, type SelectedRecipient } from './RecipientPicker';
import type {
  CaseSummary,
  ClientSummary,
  DocumentSummary,
  WaSessionInfo,
} from '../types';

type Channel = 'wa' | 'email';

/**
 * Send a document. Two recipient paths, mutually exclusive:
 *
 *   1. Original — pick a Client from the dropdown (and optionally a Case).
 *   2. Alternate — saved Contact OR typed-in "new recipient" via the
 *      RecipientPicker. Only active when no Client is selected. Picking
 *      one clears the other.
 *
 * Channel (WA / Email) auto-disables when the chosen recipient lacks the
 * relevant contact field. Routes to /api/messages/{wa,email} with the
 * appropriate clientId / contactId / newRecipient field.
 */
export function SendDocumentModal({
  document: docProp,
  documents,
  firmId,
  initialChannel,
  onSent,
  onClose,
}: {
  /** Single-doc mode. Mutually exclusive with `documents`. */
  document?: DocumentSummary;
  /** Bulk mode — when provided, the modal loops the send across all docs
   *  with one shared recipient + caption. Skipped if empty. */
  documents?: DocumentSummary[];
  firmId: string;
  /** When provided, opens the modal with this channel preselected. */
  initialChannel?: Channel;
  onSent: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['email', 'wa', 'documents', 'common', 'recipients']);
  // Pick a representative doc for subject defaults etc. In bulk mode the
  // first doc seeds the subject; the user can edit before sending.
  const doc: DocumentSummary | undefined = docProp ?? documents?.[0];
  const bulkDocs = documents ?? null;
  const isBulk = !!bulkDocs && bulkDocs.length > 1;
  if (!doc) {
    // Defensive: caller must pass either `document` or non-empty `documents`.
    throw new Error('SendDocumentModal: must pass `document` or non-empty `documents`');
  }

  // Channel availability
  const [waInfo, setWaInfo] = useState<WaSessionInfo | null>(null);
  const waConnected = waInfo?.status === 'connected';

  // Original client dropdown
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [clientId, setClientId] = useState('');
  const [caseId, setCaseId] = useState('');

  // Alternate recipient (saved Contact or typed-in "new")
  const [altRecipient, setAltRecipient] = useState<SelectedRecipient | null>(null);

  // Per-channel form state
  const [channel, setChannel] = useState<Channel>(initialChannel ?? 'email');
  const [subject, setSubject] = useState(doc.name);
  const [emailBody, setEmailBody] = useState('');
  const [waCaption, setWaCaption] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial loads
  useEffect(() => {
    apiFetch<WaSessionInfo>(`/api/firms/${firmId}/wa/status`)
      .then(setWaInfo)
      .catch(() => setWaInfo(null));
    apiFetch<{ items: ClientSummary[] }>('/api/clients')
      .then((d) => setClients(d.items))
      .catch(() => setClients([]));
  }, [firmId]);

  // Cases dropdown depends on chosen client
  useEffect(() => {
    if (!clientId) {
      setCases([]);
      setCaseId('');
      return;
    }
    apiFetch<{ items: CaseSummary[] }>(`/api/cases?clientId=${clientId}`)
      .then((d) => setCases(d.items))
      .catch(() => setCases([]));
    setCaseId('');
  }, [clientId]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId) ?? null,
    [clients, clientId],
  );

  // Mutual exclusion: picking a client clears the alt picker, and vice versa.
  function handleClientChange(id: string) {
    setClientId(id);
    if (id) setAltRecipient(null);
  }
  function handleAltChange(next: SelectedRecipient | null) {
    setAltRecipient(next);
    if (next) setClientId('');
  }

  // Resolve effective recipient: client-side priority is selectedClient
  // if set, else altRecipient.
  const recipientEmail = selectedClient?.email ?? altRecipient?.email ?? null;
  const recipientPhone = selectedClient?.phone ?? altRecipient?.phone ?? null;
  const hasRecipient = !!selectedClient || !!altRecipient;
  const canEmail = !!recipientEmail;
  const canWa = waConnected && !!recipientPhone;

  // Auto-fall-back to the usable channel after a recipient switch.
  useEffect(() => {
    if (channel === 'wa' && !canWa && canEmail) setChannel('email');
    if (channel === 'email' && !canEmail && canWa) setChannel('wa');
  }, [canEmail, canWa, channel]);

  function recipientPayload(): Record<string, unknown> {
    if (selectedClient) return { clientId: selectedClient.id };
    if (!altRecipient) return {};
    if (altRecipient.kind === 'client') return { clientId: altRecipient.clientId };
    if (altRecipient.kind === 'contact') return { contactId: altRecipient.contactId };
    return {
      newRecipient: {
        name: altRecipient.name,
        email: altRecipient.email,
        phone: altRecipient.phone,
      },
    };
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!hasRecipient) return;
    setBusy(true);
    setError(null);
    try {
      // In bulk mode, loop sequentially. We POST per-doc — the existing
      // /messages/{wa,email} routes accept a single `documentId`.
      // Sequential (not parallel) so a single failure halts the run with
      // a sensible row already-sent count.
      const docs = bulkDocs && bulkDocs.length > 0 ? bulkDocs : [doc];
      for (const d of docs) {
        if (!d) continue;
        if (channel === 'wa') {
          const body: Record<string, unknown> = {
            ...recipientPayload(),
            documentId: d.id,
          };
          if (caseId && selectedClient) body['caseId'] = caseId;
          if (waCaption.trim()) body['body'] = waCaption.trim();
          await apiFetch('/api/messages/wa', { method: 'POST', body: JSON.stringify(body) });
        } else {
          const body: Record<string, unknown> = {
            ...recipientPayload(),
            documentId: d.id,
            subject: subject.trim(),
            body: emailBody.trim(),
          };
          if (caseId && selectedClient) body['caseId'] = caseId;
          await apiFetch('/api/messages/email', { method: 'POST', body: JSON.stringify(body) });
        }
      }
      onSent();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'unknown';
      const key = `errors.${code}`;
      const translated = t(`email:${key}`);
      const fallback = t(`wa:${key}`);
      setError(
        translated !== `email:${key}`
          ? translated
          : fallback !== `wa:${key}`
            ? fallback
            : t('common:errors.with_message', { message: code }),
      );
    } finally {
      setBusy(false);
    }
  }

  const submitDisabled =
    busy ||
    !hasRecipient ||
    (channel === 'email' && (!canEmail || !subject.trim() || !emailBody.trim())) ||
    (channel === 'wa' && !canWa);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">
                {isBulk
                  ? t('documents:send.title_bulk', {
                      count: bulkDocs?.length ?? 0,
                      defaultValue: 'Send {{count}} documents',
                    })
                  : t('documents:send.title', { defaultValue: 'Send document' })}
              </div>
              <div className="form-preview-type" dir="auto">
                {isBulk
                  ? bulkDocs!.map((d) => d.name).join(' · ')
                  : doc.name}
              </div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="section-label">
              {t('documents:send.channel', { defaultValue: 'Channel' })}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className={`type-btn ${channel === 'email' ? 'active' : ''}`}
                onClick={() => setChannel('email')}
                disabled={!canEmail}
                style={{ flex: 1 }}
              >
                ✉ {t('email:modal.title')}
                {!canEmail && hasRecipient && (
                  <span style={{ fontSize: 10, color: 'var(--text3)', display: 'block' }}>
                    {t('documents:send.no_email', { defaultValue: 'no email on file' })}
                  </span>
                )}
              </button>
              <button
                type="button"
                className={`type-btn ${channel === 'wa' ? 'active' : ''}`}
                onClick={() => setChannel('wa')}
                disabled={!canWa}
                style={{ flex: 1 }}
              >
                💬 WhatsApp
                {!canWa && (
                  <span style={{ fontSize: 10, color: 'var(--text3)', display: 'block' }}>
                    {!waConnected
                      ? t('documents:send.wa_not_connected', { defaultValue: 'not connected' })
                      : t('documents:send.no_phone', { defaultValue: 'no phone on file' })}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="form-section">
            <div className="field-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="field">
                <label>
                  {t('documents:send.client', { defaultValue: 'Client' })}{' '}
                  {!altRecipient && <span className="req">*</span>}
                </label>
                <select
                  value={clientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                >
                  <option value="">
                    {t('documents:send.pick_client', { defaultValue: '— pick a client —' })}
                  </option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.email ? ` · ${c.email}` : ''}
                      {c.phone ? ` · ${c.phone}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {selectedClient && cases.length > 0 && (
                <div className="field">
                  <label>
                    {t('documents:send.case', { defaultValue: 'Case (optional)' })}
                  </label>
                  <select value={caseId} onChange={(e) => setCaseId(e.target.value)}>
                    <option value="">
                      {t('documents:send.no_case', { defaultValue: '— none —' })}
                    </option>
                    {cases.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.caseNumber}
                        {c.area ? ` · ${c.area}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Alternate recipient — only meaningful when no client is selected. */}
              {!selectedClient && (
                <div className="field">
                  <label>
                    {t('documents:send.or_other', {
                      defaultValue: 'Or send to a contact / new recipient',
                    })}
                  </label>
                  <RecipientPicker
                    value={altRecipient}
                    onChange={handleAltChange}
                    excludeClients
                  />
                </div>
              )}

              {channel === 'email' ? (
                <>
                  <div className="field">
                    <label>
                      {t('email:modal.subject')} <span className="req">*</span>
                    </label>
                    <input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                    />
                  </div>
                  <div className="field">
                    <label>
                      {t('email:modal.body')} <span className="req">*</span>
                    </label>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      placeholder={t('email:modal.body_placeholder')}
                      rows={5}
                      required
                    />
                  </div>
                </>
              ) : (
                <div className="field">
                  <label>
                    {t('documents:send.wa_caption', { defaultValue: 'Caption (optional)' })}
                  </label>
                  <textarea
                    value={waCaption}
                    onChange={(e) => setWaCaption(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <div className="section-label">{t('email:modal.attachment')}</div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>📎 {doc.name}</div>
          </div>

          {error && (
            <div className="form-section" style={{ color: 'var(--red)' }}>
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={busy}
            >
              {t('common:actions.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button type="submit" className="btn-primary" disabled={submitDisabled}>
              {busy
                ? t('email:modal.submit_busy')
                : t('documents:send.send', { defaultValue: 'Send' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
